import type TextToSVG from 'text-to-svg';
import type { GenerationOptions } from 'text-to-svg';

import {
  type EdgeData,
  type RenderableEdgeData,
  isRenderableEdgeData,
} from '../../edges/EdgeData';
import { getEdgeLabelText } from '../../edges/EdgeLabels';
import {
  type EdgePositionsLabel,
  type EdgePositionsLine,
  isEdgeLinePoint,
  isEdgeLineQuadraticBezier,
} from '../../edges/EdgePositions';
import {
  type LinePattern,
  getEdgeStyleArrowHeadColor,
  getEdgeStyleCircleColor,
  getEdgeStyleLineColor,
  getEdgeStyleLinePattern,
  getEdgeStyleThickness,
} from '../../edges/EdgeStyle';
import { EDGE_LABEL_FONT_SIZE } from '../../edges/constants';
import { getEdgeConvexHullPoints } from '../../edges/positions';
import { type Point, Rectangle, polygon, rectangle } from '../../elements';
import type { NodeId } from '../../nodes';
import { SvgPathBuilder } from '../../svg/SvgPathBuilder';
import { anchorToSvgText, getGraphFonts } from '../../svg/text';
import { makeTag } from '../../svg/utils';
import { safeAbsurd } from '../../types/safeAbsurd';
import { convertDegreesToRadians } from '../../utils/angles';
import { rgbNumberToString } from '../../utils/rgb';
import type { GraphExportData } from './GraphExportData';
import { SVG_HIDDEN_ELEMENT_OPACITY } from './style';

const buildLinePath = (line: EdgePositionsLine): string => {
  const linePathBuilder = new SvgPathBuilder().moveTo(line[0]);
  for (const linePart of line.slice(1)) {
    if (isEdgeLineQuadraticBezier(linePart)) {
      linePathBuilder.quadraticBezierTo(linePart.c)(linePart.e);
    } else if (isEdgeLinePoint(linePart)) {
      linePathBuilder.lineTo(linePart);
    } else {
      safeAbsurd(linePart, 'unimplemented line part type');
    }
  }
  return linePathBuilder.build();
};

// empty object is a correct type here

const getEdgeDashArray = (
  thickness: number,
  pattern: LinePattern,
): { 'stroke-dasharray'?: string } => {
  switch (pattern) {
    case 'solid':
      return {};
    case 'dotted':
      return { 'stroke-dasharray': `${thickness} ${thickness}` };
    case 'dashed':
      return { 'stroke-dasharray': `${thickness * 4} ${thickness}` };
    default:
      return safeAbsurd(
        pattern,
        `not implemented edge pattern variant: ${pattern}`,
        {},
      );
  }
};

const renderEdgeLine = (edge: RenderableEdgeData): string => {
  const thickness = getEdgeStyleThickness(edge.style);
  const lineColor = rgbNumberToString(getEdgeStyleLineColor(edge.style));
  const linePattern = getEdgeStyleLinePattern(edge.style);
  return makeTag('path', {
    d: buildLinePath(edge.positions.line),
    fill: 'none',
    stroke: lineColor,
    'stroke-width': thickness,
    ...getEdgeDashArray(thickness, linePattern),
  });
};

const renderArrowHead = (
  edge: RenderableEdgeData,
  position: 'source' | 'target',
): string => {
  const path = edge.positions.arrowHeads?.[position];
  if (!path) {
    return '';
  }

  return makeTag('path', {
    d: polygon.getD(path),
    fill: rgbNumberToString(getEdgeStyleArrowHeadColor(position, edge.style)),
  });
};

const renderCircle = (edge: RenderableEdgeData): string => {
  const position = edge.positions.circle;
  const color = getEdgeStyleCircleColor(edge.style);
  if (!position || !color) {
    return '';
  }

  return makeTag('circle', {
    cx: position.position.x,
    cy: position.position.y,
    r: position.radius,
    fill: rgbNumberToString(color),
  });
};

const getEdgeLabelOptions = (
  labelPosition: EdgePositionsLabel,
): GenerationOptions => ({
  x: labelPosition.position.x,
  y: labelPosition.position.y,
  fontSize: EDGE_LABEL_FONT_SIZE,
  anchor: anchorToSvgText(labelPosition.anchor),
  attributes: {
    fill: 'black',
    transform: `rotate(${labelPosition.angle} ${labelPosition.position.x} ${labelPosition.position.y})`,
  },
});

const renderLabels = (
  edge: RenderableEdgeData,
  svgFont: TextToSVG,
): [svg: string, boundingBox: Rectangle | null] => {
  let svg = '';
  const boundingBoxPoints: Point[] = [];
  for (const labelKey of ['source', 'center', 'target'] as const) {
    const labelPositions = edge.positions.labels?.[labelKey];
    if (!labelPositions || !edge.labels) {
      continue;
    }
    const text = getEdgeLabelText(edge.labels, labelKey);
    if (!text) {
      continue;
    }

    const labelOptions = getEdgeLabelOptions(labelPositions);
    const path = svgFont.getPath(text, labelOptions);
    const labelMetrics = svgFont.getMetrics(text, labelOptions);
    const rect = rectangle.getRotatedRectangle(
      new Rectangle(labelMetrics, labelMetrics.width, labelMetrics.height),
      labelPositions.position,
      convertDegreesToRadians(labelPositions.angle),
    );
    svg += makeTag('path', {
      d: polygon.getD(rect),
      fill: 'white',
    });
    svg += path;
    boundingBoxPoints.push(...rect);
  }
  return [
    svg,
    boundingBoxPoints.length > 0
      ? rectangle.createPointsBoundingBox(...boundingBoxPoints)
      : null,
  ];
};

const renderEdge =
  (hiddenNodes: Set<NodeId> | undefined) =>
  (edge: EdgeData): [svg: string, boundingBox: Rectangle] | null => {
    if (!isRenderableEdgeData(edge)) {
      return null;
    }

    const [labels, labelsBoundingBox] = renderLabels(
      edge,
      getGraphFonts().edgeLabels,
    );

    const boundingBoxExceptLabels = rectangle.createPointsBoundingBox(
      ...getEdgeConvexHullPoints(edge.positions),
    );
    const boundingBox = labelsBoundingBox
      ? rectangle.mergeBoundingBoxes(boundingBoxExceptLabels, labelsBoundingBox)
      : boundingBoxExceptLabels;

    let edgeSvg = `${renderEdgeLine(edge)}${renderArrowHead(
      edge,
      'source',
    )}${renderArrowHead(edge, 'target')}${renderCircle(edge)}${labels}`;

    if (hiddenNodes?.has(edge.source) || hiddenNodes?.has(edge.target)) {
      edgeSvg = `<g opacity="${SVG_HIDDEN_ELEMENT_OPACITY}">${edgeSvg}</g>`;
    }

    return [edgeSvg, boundingBox];
  };

export const renderEdges = (
  graph: GraphExportData,
): [svg: string, boundingBox: Rectangle | null] => {
  const renderedEdges = Object.values(graph.edges)
    .map(renderEdge(graph.hiddenNodes))
    .filter(<T>(x: T): x is NonNullable<T> => x !== null);

  const edgesSvg = renderedEdges.map(([svg]) => svg).join('');
  const edgeBBs =
    renderedEdges.length > 0
      ? rectangle.mergeBoundingBoxes(...renderedEdges.map(([, bb]) => bb))
      : null;

  return [edgesSvg, edgeBBs];
};
